import type { Buffer, Cell, Column, Row, Workbook } from 'exceljs';
import saveAs from 'file-saver';
import { first, isNil, mapKeys, mapValues, tail } from 'lodash';
import { parse, type ParseConfig } from 'papaparse';
import { asyncForEach } from './helpers';
import { type AtLeast } from './utility-types';

export type XSLXCell = AtLeast<Pick<Cell, 'dataValidation' | 'value'>, 'value'>;

export interface IXSLXExport<RecordFormat, RowFormat> {
  headers: AtLeast<Column, 'key' | 'header'>[];
  translate(
    records: RecordFormat[]
  ):
    | Promise<Record<keyof RowFormat, XSLXCell>[]>
    | Record<keyof RowFormat, XSLXCell>[];
}

export interface IWorkbookSheet<RecordFormat, RowFormat> {
  name: string;
  records: RecordFormat[];
  translator: IXSLXExport<RecordFormat, RowFormat>;
}

export interface IXSLXImport<RecordFormat> {
  headers: AtLeast<Column, 'key' | 'header'>[];
  translate(row: Row): Promise<RecordFormat> | RecordFormat;
}

export interface IBlobFilenamePair {
  fileName: string;
  blob: Blob;
}

export class XSLXImporterExporter {
  worksheetName = 'Sheet 1';
  mimeType =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';

  async getBlob<RecordFormat, RowFormat>(
    fileName: string,
    records: RecordFormat[],
    translator: IXSLXExport<RecordFormat, RowFormat>
  ): Promise<IBlobFilenamePair> {
    const workbook = await this.getWorkbook([
      { name: this.worksheetName, records, translator },
    ]);
    const buffer = await workbook.xlsx.writeBuffer();
    return {
      fileName: decodeURI(`${fileName}.xlsx`),
      blob: this._build(buffer),
    };
  }

  async download<RecordFormat, RowFormat>(
    fileName: string,
    records: RecordFormat[],
    translator: IXSLXExport<RecordFormat, RowFormat>
  ): Promise<void> {
    const { blob } = await this.getBlob(fileName, records, translator);
    this._save(blob, fileName);
  }

  async getWorkbook(
    sheets: IWorkbookSheet<unknown, unknown>[]
  ): Promise<Workbook> {
    // Deliberately deferred: this module is re-exported from the libs/shared
    // barrel, so a static exceljs import loads 153 exceljs + 34 jszip modules
    // into every Cloud Function at startup - including ones that never touch a
    // spreadsheet. Keep this dynamic unless the barrel stops re-exporting it.
    const { Workbook: WorkbookCtor } = await import('exceljs');
    const workbook = new WorkbookCtor();

    await asyncForEach(sheets, async (sheetDef) => {
      const sheet = workbook.addWorksheet(sheetDef.name);
      sheet.columns = sheetDef.translator.headers;

      const rows = await sheetDef.translator.translate(sheetDef.records);

      rows.map((cells: Record<string, XSLXCell>) => {
        const row = sheet.addRow(mapValues(cells, (cell) => cell.value));

        mapValues(cells, (cell, key) => {
          if (cell.dataValidation) {
            row.getCell(key).dataValidation = cell.dataValidation;
          }
        });
      });
    });

    return workbook;
  }

  async parse<RecordFormat>(
    file: Blob,
    translator: IXSLXImport<RecordFormat>
  ): Promise<RecordFormat[]> {
    // Deferred for the same reason as getWorkbook() above - the libs/shared
    // barrel would otherwise pull exceljs into every Cloud Function's startup.
    const { Workbook: WorkbookCtor } = await import('exceljs');
    const workbook = new WorkbookCtor();

    if (file.type === 'text/csv') {
      await this._addCSVWorksheet(workbook, file, translator);
    } else {
      await this._addXLXSWorksheets(workbook, file, translator);
    }

    const sheet = first(workbook.worksheets);
    if (!sheet) {
      // eslint-disable-next-line no-console
      console.warn('No worksheet found in the provided file.');
      return [];
    }

    const rows: Row[] = [];
    sheet.eachRow((row) => rows.push(row));

    // eachRow includes the header row - drop it so only data rows translate
    return asyncForEach(tail(rows), async (row) => translator.translate(row));
  }

  private async _addXLXSWorksheets<RecordFormat>(
    workbook: Workbook,
    file: Blob,
    translator: IXSLXImport<RecordFormat>
  ): Promise<void> {
    await workbook.xlsx.load(await file.arrayBuffer());
    for (const sheet of workbook.worksheets) {
      sheet.columns = translator.headers;
    }
  }

  private async _addCSVWorksheet<RecordFormat>(
    workbook: Workbook,
    file: Blob,
    translator: IXSLXImport<RecordFormat>
  ): Promise<void> {
    const text = await file.text();
    const worksheet = workbook.addWorksheet(this.worksheetName);

    const rows = await new Promise<object[]>((resolve) => {
      const config: ParseConfig = {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => header.trim().replace(/"/g, ''),
        complete: (results) => resolve(results.data),
      };
      parse(text, config);
    });

    if (!rows.length) {
      return;
    }

    const keys = Object.keys(rows[0]);
    const columns = keys.map((header) => ({
      key:
        translator.headers.find(
          (translatorHeader) => translatorHeader.header === header
        )?.key || header,
      header,
    }));

    const rowData = rows.map((row) =>
      mapKeys(row, (_value, key) => {
        const column = columns.find((col) => col.header === key);
        return column ? column.key : key;
      })
    );

    worksheet.columns = columns;
    for (const row of rowData) {
      const isEmpty = Object.values(row).every(
        (value) => isNil(value) || value === ''
      );
      if (!isEmpty) {
        worksheet.addRow(row);
      }
    }
  }

  private _build(buffer: Buffer): Blob {
    return new Blob([buffer], {
      type: this.mimeType,
    });
  }

  private _save(fileBlob: Blob, fileName: string): void {
    saveAs(fileBlob, decodeURI(`${fileName}.xlsx`));
  }
}
